import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, CheckCircle2, ShieldAlert, PenLine } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { api, prettyTs } from "./api";

export default function PortalContracts() {
  const [data, setData] = useState(null);
  const [names, setNames] = useState({});
  const [consents, setConsents] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/contracts").then(({ data }) => setData(data)).catch((e) => toast.error(e?.response?.data?.detail || "Could not load agreements"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sign = async (contractId) => {
    setBusy(contractId);
    try {
      await api.post(`/portal/contracts/${contractId}/sign`, {
        typed_name: names[contractId],
        consent: !!consents[contractId],
      });
      toast.success("Agreement signed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signature failed");
    } finally {
      setBusy(null);
    }
  };

  const pending = data?.contracts.filter((c) => c.required && !c.signed) || [];

  return (
    <PortalShell title="Partnership Agreements">
      <div className="mb-6 rounded-xl border border-aqua/40 bg-aqua/[0.07] p-5" data-testid="insurance-notice">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-aqua">Insurance Carrier — XINSURANCE</p>
        <p className="mt-2 text-sm leading-relaxed text-offwhite">
          Insurance coverage for the fleet partnership program is provided through <strong>XINSURANCE</strong>. The
          Xtreme Exotics Watersports LLC fleet-partnership agreements below govern <strong>possession, operations,
          insurance, maintenance, incident reporting, and revenue sharing</strong> between Xtreme Exotics Watersports
          LLC and the Fleet Partner.
        </p>
        <p className="mt-3 border-t border-aqua/20 pt-3 text-sm leading-relaxed text-offwhite/90">
          The Fleet Partner is added as an <strong>additional insured</strong> on the policy and is required to
          contribute <strong>45% toward the deductible, not to exceed $2,500</strong>. Coverage is primary commercial
          watercraft/rental liability and physical-damage coverage for owner-titled Jet Cars placed with independent
          rental operators.
        </p>
      </div>
      {pending.length > 0 ? (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-orange-500/40 bg-orange-400/10 p-5" data-testid="contracts-banner">
          <ShieldAlert size={20} className="mt-0.5 shrink-0 text-orange-600" />
          <p className="text-sm leading-relaxed text-offwhite">
            <strong>{pending.length} required agreement{pending.length > 1 ? "s" : ""} unsigned.</strong>{" "}
            <span className="text-muted">All required agreements must be signed before a jet car can be deployed to your location — reservation creation is locked until then.</span>
          </p>
        </div>
      ) : data && data.contracts.length > 0 ? (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-500/40 bg-emerald-400/10 p-5" data-testid="contracts-clear">
          <CheckCircle2 size={20} className="shrink-0 text-emerald-700" />
          <p className="text-sm font-semibold text-offwhite">All required agreements signed — you are cleared for deployment.</p>
        </div>
      ) : null}

      {!data ? (
        <p className="py-16 text-center text-sm text-muted">Loading…</p>
      ) : data.contracts.length === 0 ? (
        <div className="card-line rounded-xl p-12 text-center" data-testid="contracts-empty">
          <FileSignature size={22} className="mx-auto text-aqua" />
          <p className="mt-4 text-sm text-muted">No agreements published yet. XE will publish the partnership agreements here for signature.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="contracts-list">
          {data.contracts.map((c) => (
            <div key={c.contract_id} className="card-line rounded-xl p-6" data-testid={`contract-${c.contract_id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-display text-lg font-bold text-offwhite">{c.title}</h2>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted">v{c.version}</span>
                {c.required && <span className="rounded-full border border-black/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-muted">Required</span>}
                <span className={`ml-auto rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${c.signed ? "border-emerald-500/40 bg-emerald-400/10 text-emerald-700" : "border-orange-500/40 bg-orange-400/10 text-orange-700"}`} data-testid={`contract-status-${c.contract_id}`}>
                  {c.signed ? "Signed" : "Awaiting signature"}
                </span>
              </div>

              <div className="mt-4 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border border-black/[0.08] bg-ink/50 p-5 text-sm leading-relaxed text-offwhite/85" data-testid={`contract-body-${c.contract_id}`}>
                {c.body}
              </div>

              {c.signed ? (
                <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-400/5 p-4 text-sm" data-testid={`contract-signed-${c.contract_id}`}>
                  <p className="font-semibold text-offwhite">Signed electronically by {c.signature.typed_name}</p>
                  <p className="mt-1 text-xs text-muted">{c.signature.signed_by_email} · {prettyTs(c.signature.signed_at)} · Version {c.version}</p>
                </div>
              ) : (
                <div className="mt-4 border-t border-black/[0.08] pt-4">
                  <p className="mb-3 text-[11px] leading-relaxed text-muted/80">{data.esign_consent_text}</p>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="flex-1">
                      <label className="field-label" htmlFor={`sign-${c.contract_id}`}>Type your full legal name to sign</label>
                      <input
                        id={`sign-${c.contract_id}`}
                        className="field font-display text-lg italic"
                        placeholder="Full legal name"
                        value={names[c.contract_id] || ""}
                        onChange={(e) => setNames((n) => ({ ...n, [c.contract_id]: e.target.value }))}
                        data-testid={`sign-name-${c.contract_id}`}
                      />
                    </div>
                    <label className="flex cursor-pointer items-center gap-2.5 pb-2 text-xs text-muted">
                      <input
                        type="checkbox"
                        checked={!!consents[c.contract_id]}
                        onChange={(e) => setConsents((s) => ({ ...s, [c.contract_id]: e.target.checked }))}
                        className="h-5 w-5 accent-[#00A8A2]"
                        data-testid={`sign-consent-${c.contract_id}`}
                      />
                      I consent to electronic signature
                    </label>
                    <button
                      onClick={() => sign(c.contract_id)}
                      disabled={busy === c.contract_id || !(names[c.contract_id] || "").trim() || !consents[c.contract_id]}
                      className="btn-primary !px-6 disabled:opacity-40"
                      data-testid={`sign-submit-${c.contract_id}`}
                    >
                      <PenLine size={15} /> {busy === c.contract_id ? "Signing…" : "Sign Agreement"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </PortalShell>
  );
}
